import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {useCachedPropertyGroupsController} from "../../../../../hooks/use-industry";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import axios from "axios";
import Api from "../../../../../../core/services/api/api";
import {Box, Button, IconButton, Skeleton, SvgIcon, Tooltip, Typography} from "@mui/material";
import UIUtils from "../../../../../utils";
import {Link} from "react-router-dom";
import {CloseOutlined, ControlCamera, EditOutlined, VisibilityOutlined} from "@mui/icons-material";
import Grid2 from "@mui/material/Unstable_Grid2";
import LazyImage from "../../../lazy-image";
import {Tab, Tabs} from "../../../../base/Tabs";
import AvailabilitySection from "../sections/availability";
import PricesSection from "../sections/prices";
import TechnicalInformationSection from "../sections/technical-information";
import PropertyGroupInformationSection from "../sections/property-group-information";
import NotesSection from "../sections/notes";
import {ReactComponent as ProblemSvg} from "../../../../../../assets/images/art-like/problem.svg";
import CopyToClipboard from "../../../../base/copy-to-clipboard";
import {ReactComponent as Copy} from "../../../../../../assets/images/copy.svg";
import {ProductTitleText} from "../../../../../views/panel/operator/products/single";
import DeliveryModeChip from "../../../delivery-mode-chip";


export const productInformationPopoverTabs = {
    availability: 'availability',
    prices: 'prices',
    technicalInfo: 'technicalInfo',
    notes: 'notes',
}

const ProductInformation = ({partNo, close, popperInstance, startingTab, width, height}) => {
    const [loadingProductInfo, setLoadingProductInfo] = useState(true);
    const [productInfo, setProductInfo] = useState(null);
    const [propertyOrders, setPropertyOrders] = useState([])
    const [loadingGroupProperties, setLoadingGroupProperties] = useState(true);
    const [propertyGroups, setPropertyGroups] = useState([]);
    const operatorAccessLevels = useAccessLevels();
    const cache = useRef({});

    const defaultTab = useMemo(() =>
            operatorAccessLevels.packages.bizInventory.availabilityInLookup
                ? productInformationPopoverTabs.availability
                : operatorAccessLevels.packages.sales.pricesInLookup
                    ? productInformationPopoverTabs.prices
                    : productInformationPopoverTabs.technicalInfo,
        [operatorAccessLevels.packages.bizInventory.availabilityInLookup, operatorAccessLevels.packages.sales.pricesInLookup]);

    const [tab, setTab] = useState(startingTab
        ? startingTab === productInformationPopoverTabs.availability && !operatorAccessLevels.packages.bizInventory.availabilityInLookup
            ? defaultTab
            : startingTab === productInformationPopoverTabs.prices && !operatorAccessLevels.packages.sales.pricesInLookup
                ? defaultTab
                : startingTab
        : defaultTab);
    const {getPropertyGroupsForIndustry} = useCachedPropertyGroupsController();
    const isMounted = useIsMounted();

    const viewProductLink = useMemo(() => routeFunctions.lookup.single(partNo), [partNo]);

    const editProductLink = useMemo(() => routeFunctions.products.single(productInfo?.id ?? 0), [productInfo?.id]);

    /**
     * Clears the cache when the part number changes.
     */
    useEffect(() => {
        cache.current = {};
    }, [partNo])

    /**
     * Fetches the property groups of the products from the server.
     * @type {(function(*, *): Promise<void>)}
     */
    const getPropertyGroups = useCallback(async (industryId, cancelTokenSource) => {
        setLoadingGroupProperties(true);
        const propertyGroups = await getPropertyGroupsForIndustry(industryId, cancelTokenSource.token);
        if (!isMounted())
            return;
        if (!cancelTokenSource.token?.reason) {
            setLoadingGroupProperties(false);
        }
        setPropertyGroups(propertyGroups ?? []);
    }, [getPropertyGroupsForIndustry, isMounted])
    /**
     * As soon as the component is mounted:
     * - fetches the technical properties of the part.
     */
    useEffect(() => {
        if (!productInfo?.industryId)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getPropertyGroups(productInfo?.industryId, cancelTokenSource);
        return () => cancelTokenSource.cancel();
    }, [getPropertyGroups, productInfo?.industryId])

    /**
     * Fetches the product's key information from the server.
     * @type {(function(string): Promise<void>)|*}
     */
    const getProductKeyInformation = useCallback(async (partNo) => {
        if (!partNo)
            return;
        const response = await Api.getProductKeyInformation(partNo);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProductInfo(prevState => ({
                ...prevState,
                title: response.data.title ?? "",
                deliveryMode: response.data.deliveryMode,
            }));
        }
    }, [isMounted])

    /**
     * Fetch the product information.
     * @type {(function(): Promise<void>)}
     */
    const fetchProductInformation = useCallback(async () => {
        setLoadingProductInfo(true);
        const response = await Api.getPartInformation(partNo);
        if (!isMounted())
            return;
        setLoadingProductInfo(false);
        if (response?.resultFlag) {
            setProductInfo(response?.data);
            void getProductKeyInformation(partNo);
        }
    }, [getProductKeyInformation, isMounted, partNo])

    /**
     * Fetches the property orders of the industry.
     * @type {(function(*): Promise<void>)}
     */
    const fetchPropertyOrders = useCallback(async (industryId, cancelToken) => {
        const response = await Api.getIndustryPropertyExtraConfigs(industryId, cancelToken);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPropertyOrders(response?.data?.propertiesOrders ?? []);
        }
    }, [isMounted])

    /**
     * When the industry changes, fetch the property orders.
     */
    useEffect(() => {
        if (!productInfo?.industryId)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchPropertyOrders(productInfo?.industryId, cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [productInfo?.industryId, fetchPropertyOrders])

    /**
     * Updates the cache.
     * @type {(function(*, *): void)}
     */
    const updateCache = useCallback((key, value) => {
        cache.current[key] = value;
    }, [])

    /**
     * Fetch the product information on mount.
     */
    useEffect(() => {
        void fetchProductInformation();
    }, [fetchProductInformation]);

    return (
        <>
            <Box
                className={'d-flex flex-column'}
                sx={{
                    width: `min(90vw, ${width}px)`,
                    maxHeight: `min(90vh, ${height}px)`,
                    overflowY: 'auto',
                    position: 'relative',
                    gap: 2,
                    padding: 1,
                }}
            >
                <Box sx={{
                    position: 'sticky',
                    top: -8,
                    zIndex: 3,
                    left: 0,
                    backgroundColor: 'background.paper',
                    padding: 1,
                    paddingInlineEnd: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    cursor: 'grab',
                    '&:active': {
                        cursor: 'grabbing',
                    },
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5}}>
                            <Tooltip title={"You can move this popover through the panel."} placement={'right'} followCursor>
                                <ControlCamera color={'primary'}/>
                            </Tooltip>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}} className={'draggable-popover-cancel'}>
                                <Typography variant={'body2'} component={'span'} color={'text.secondary'} fontWeight={800} sx={{cursor: 'text'}}>
                                    {productInfo?.parentCategory?.title ?? ""} {productInfo?.parentCategory?.title && productInfo?.category?.title ? '>' : ''} {productInfo?.category?.title ?? ""}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5}}>
                            <Box className={'draggable-popover-cancel'} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}>
                                {
                                    operatorAccessLevels.packages.lookup.productInformation &&
                                    !!productInfo?.id &&
                                    <Button
                                        variant={'text'}
                                        size={'small'}
                                        color={'primary'}
                                        sx={{display: 'flex', gap: 0.5, alignItems: 'center',}}
                                        component={Link}
                                        target={'_blank'}
                                        to={editProductLink}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <EditOutlined/>
                                        Edit
                                    </Button>
                                }
                                {
                                    operatorAccessLevels.packages.lookup.lookupProductInformation &&
                                    !!productInfo?.id &&
                                    !!partNo &&
                                    <Button
                                        variant={'text'}
                                        size={'small'}
                                        color={'primary'}
                                        sx={{display: 'flex', gap: 0.5, alignItems: 'center',}}
                                        component={Link}
                                        target={'_blank'}
                                        to={viewProductLink}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <VisibilityOutlined/>
                                        view
                                    </Button>
                                }
                            </Box>
                            <IconButton onClick={close} color={'error'}>
                                <CloseOutlined/>
                            </IconButton>
                        </Box>

                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography component={'div'} variant={'body1'} fontWeight={600} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            {
                                productInfo?.industryId &&
                                React.cloneElement(UIUtils.getIndustryIcon(null, productInfo?.industryId) ?? <div/>, {
                                    style: {width: 30, height: 30, marginInlineEnd: 8},
                                    className: 'tertiary-main',
                                })
                            }
                            <ProductTitleText
                                className={'draggable-popover-cancel'}
                                loading={loadingProductInfo}
                                partNo={partNo ?? ""}
                                title={productInfo?.title ?? ""}
                                showPartNoPopover={false}
                                PartNoWrapper={({children}) => (
                                    <CopyToClipboard>
                                        {({copy}) => (
                                            <Box component={'span'} sx={{width: 'fit-content', cursor: 'text'}}>
                                                {children}
                                                <IconButton
                                                    disableRipple
                                                    component={'span'}
                                                    onClick={() => copy(partNo ?? "")}
                                                    color={'paper.secondary'}
                                                    className={'copy-to-clipboard-button ml-2 p-0'}
                                                >
                                                    <SvgIcon fontSize={'small'} className={'icon small'} component={Copy}/>
                                                </IconButton>
                                            </Box>
                                        )}
                                    </CopyToClipboard>
                                )}
                                TitleWrapper={({children}) => (
                                    <CopyToClipboard>
                                        {({copy}) => (
                                            <Box component={'span'} sx={{width: 'fit-content', cursor: 'text'}}>
                                                {children}
                                                <IconButton
                                                    disableRipple
                                                    component={'span'}
                                                    onClick={() => copy(productInfo?.title ?? "")}
                                                    color={'paper.secondary'}
                                                    className={'copy-to-clipboard-button ml-2 p-0'}
                                                >
                                                    <SvgIcon fontSize={'small'} className={'icon small'} component={Copy}/>
                                                </IconButton>
                                            </Box>
                                        )}
                                    </CopyToClipboard>
                                )}
                            />
                        </Typography>
                    </Box>
                    {
                        !loadingProductInfo &&
                        <Box sx={{mt: 1.25}}>
                            <DeliveryModeChip deliveryMode={productInfo?.deliveryMode} size={'small'}/>
                        </Box>
                    }
                </Box>
                {
                    !productInfo && !loadingProductInfo &&
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBlock: 10
                    }}>
                        <ProblemSvg
                            className={'w-100'}
                        />
                        <Typography variant={'body1'} color={'text.secondary'} fontWeight={600}>
                            Product not found.
                        </Typography>
                        <Typography variant={'body2'} color={'text.secondary'}>
                            The product with part number {partNo} was not found in our system.
                        </Typography>
                    </Box>
                }
                {
                    !(!productInfo && !loadingProductInfo) &&
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, paddingBottom: 2}} className={'draggable-popover-cancel'}>
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12} md={5}>
                                {
                                    loadingProductInfo &&
                                    <Skeleton
                                        sx={{transform: 'none'}}
                                        variant={'rounded'}
                                        height={200}
                                        width={'100%'}
                                    />
                                }
                                {
                                    !loadingProductInfo &&
                                    <LazyImage
                                        src={(productInfo?.gallery?.find(e => e.isCover) ?? productInfo?.gallery?.[0])?.url}
                                        alt={`Product Image of ${partNo}`}
                                        onContextMenu={(e) => e.preventDefault()}
                                        onDragStart={(e) => e.preventDefault()}
                                        className={'disable-downloading-image'}
                                        style={{
                                            objectFit: 'contain',
                                            width: '100%',
                                            objectPosition: 'top',
                                            borderRadius: 8,
                                        }}
                                        wrapperProps={{
                                            style: {
                                                height: 200,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                            },
                                        }}
                                        height={200}
                                    />
                                }
                            </Grid2>
                            <Grid2 xs={12} md={7}>
                                {
                                    loadingProductInfo &&
                                    <Box className={'d-flex flex-column'} sx={{gap: 0.5}}>
                                        {
                                            Array.from({length: 5}).map((_, index) => (
                                                <Skeleton
                                                    key={`loading-${index}`}
                                                    sx={{transform: 'none'}}
                                                    variant={'rounded'}
                                                    height={25}
                                                    width={index === 4 ? '20%' : '100%'}
                                                />
                                            ))
                                        }
                                    </Box>

                                }
                                {
                                    !loadingProductInfo &&
                                    <Box
                                        dangerouslySetInnerHTML={{__html: productInfo?.description ?? ""}}
                                        sx={(theme) => ({
                                            maxHeight: 200,
                                            height: 200,
                                            overflowY: 'hidden',
                                            overflowX: 'auto',
                                            maxWidth: '100%',
                                            position: 'relative',
                                            '&:after': {
                                                zIndex: 2,
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                height: 100,
                                                background: `linear-gradient(180deg, rgba(255,255,255,0) 0%, ${theme.palette.background.paper} 100%)`,
                                            },
                                        })}
                                    />
                                }
                            </Grid2>
                        </Grid2>
                        <Tabs
                            value={tab ?? false}
                            onChange={(_, tab) => setTab(tab)}
                        >
                            {
                                operatorAccessLevels.packages.bizInventory.availabilityInLookup &&
                                <Tab value={productInformationPopoverTabs.availability} label="Availability"/>
                            }
                            {
                                operatorAccessLevels.packages.sales.pricesInLookup &&
                                <Tab value={productInformationPopoverTabs.prices} label="Prices"/>
                            }
                            <Tab value={productInformationPopoverTabs.technicalInfo} label="Technical Information"/>
                            {
                                !loadingGroupProperties &&
                                propertyGroups.map((propertyGroup) => (
                                    <Tab
                                        key={propertyGroup.id}
                                        value={`property-group-${propertyGroup.id}`}
                                        label={propertyGroup.title}
                                    />
                                ))
                            }
                            {
                                productInfo?.notes?.length > 0 &&
                                <Tab value={productInformationPopoverTabs.notes} label="Notes"/>
                            }
                        </Tabs>
                        <Box>
                            {
                                tab === productInformationPopoverTabs.availability &&
                                operatorAccessLevels.packages.bizInventory.availabilityInLookup &&
                                <AvailabilitySection
                                    partNo={partNo}
                                    partId={productInfo?.id}
                                    cache={cache.current}
                                    updateCache={updateCache}
                                />
                            }
                            {
                                !!productInfo?.id &&
                                tab === productInformationPopoverTabs.prices &&
                                operatorAccessLevels.packages.sales.pricesInLookup &&
                                <PricesSection
                                    partId={productInfo?.id}
                                    partNo={partNo}
                                    cache={cache.current}
                                    updateCache={updateCache}
                                />
                            }
                            {
                                !!productInfo?.id &&
                                tab === productInformationPopoverTabs.technicalInfo &&
                                <TechnicalInformationSection
                                    partId={productInfo?.id}
                                    cache={cache.current}
                                    updateCache={updateCache}
                                    popperInstance={popperInstance}
                                    propertyOrders={propertyOrders}
                                />
                            }
                            {
                                !!productInfo?.id &&
                                tab.startsWith('property-group-') &&
                                <PropertyGroupInformationSection
                                    partId={productInfo?.id}
                                    groupId={tab.replace('property-group-', '')}
                                    cache={cache.current}
                                    updateCache={updateCache}
                                />
                            }
                            {
                                tab === productInformationPopoverTabs.notes &&
                                productInfo?.notes?.length > 0 &&
                                <NotesSection notes={productInfo?.notes}/>
                            }
                        </Box>
                    </Box>
                }
            </Box>
        </>
    );
}

export default ProductInformation;